import React, { memo, useEffect, useState } from 'react';
import { Options, Point } from 'highcharts';
import Highcharts from 'highcharts/esm/highcharts.js';
import CustomEvents from 'highcharts-custom-events';
import { HighchartsReact } from 'highcharts-react-official';
import ReactDOMServer from 'react-dom/server';
import { siteColours } from '@components-clean/core/charts/line/utils';
import { FormatNumberForChart } from '@components-clean/core/charts/shared/number-utils';
import { CHART_HEIGHT_PIXELS, getCreditsStyle, getChartEvents } from '@components-clean/core/charts/shared/utils';
import Loading from '@components-clean/core/loading/loading';
import Tooltip from '@components-clean/core/tooltip';
import { ChartHorizontalLine, HighchartsWithEvents, LineSeriesData } from '@interfaces-clean/panels.interface';
import { getThemeColours } from '@utils-clean/highchart-options/highchart-themes';
import { PanelSeriesType, PanelTheme } from 'types/graphql-global-types';
import { useTranslation } from '@hooks/index';
import { stringDateWithoutTimeFromUtc } from '@utils/convertor';

CustomEvents(Highcharts);

export interface LineChartProps {
    categories?: string[];
    horizontalLines?: ChartHorizontalLine[];
    isDateRange?: boolean;
    panelData: LineSeriesData[];
    seriesType: PanelSeriesType;
    chartRef?: React.RefObject<HighchartsReact.RefObject | null>;
    showLegend?: boolean;
    stacking?: 'normal' | 'percent';
    subTitle?: string;
    theme?: PanelTheme;
    title?: string;
    xTitle?: string;
    yTitle?: string;
    showSubtitle?: boolean;
    showxAxisLabels?: boolean;
    customOptions?: Options;
    showWaterMark?: boolean;
    showCrosshairs?: boolean;
    minY?: number;
    maxY?: number;
}

const TURBO_THRESHOLD = 2000;

export const Chart = (props: LineChartProps) => {
    const {
        title,
        panelData,
        categories,
        seriesType,
        chartRef,
        showLegend,
        stacking,
        theme = PanelTheme.NONE,
        xTitle,
        yTitle,
        isDateRange,
        subTitle,
        horizontalLines,
        showSubtitle = true,
        showxAxisLabels = true,
        customOptions = undefined,
        showWaterMark = false,
        showCrosshairs = true,
        minY,
        maxY,
    } = props;
    const [open, setOpen] = useState<boolean>(false);
    const [targetElement, setTargetElement] = useState<EventTarget | null>(null);
    const [options, setOptions] = useState<HighchartsWithEvents | null>(null);
    const { t } = useTranslation();

    useEffect(() => {
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/set-state-in-effect
        setOptions(
            Highcharts.merge(
                {
                    exporting: {
                        allowHTML: true,
                        chartOptions: {
                            title: { text: title, style: { fontWeight: 'semi-bold' } },
                            subtitle: {
                                text: subTitle,
                                useHTML: true,
                                verticalAlign: 'bottom',
                                align: 'center',
                            },
                            credits: { enabled: false },
                            chart: {
                                height: 600,
                                width: 600,
                            },
                            legend: {
                                maxHeight: 400,
                                width: seriesType !== PanelSeriesType.SITE_AVERAGE ? 550 : undefined,
                                itemWidth: undefined,
                                layout: 'horizontal',
                            },
                        },
                    },
                    chart: {
                        zooming: {
                            type: 'xy',
                        },
                        height: CHART_HEIGHT_PIXELS,
                        events: getChartEvents(showWaterMark, showSubtitle, title, subTitle, {
                            fullScreenOptions: {
                                legend: {
                                    maxHeight: 200,
                                },
                            },
                            defaultOptions: {
                                legend: {
                                    maxHeight: 100,
                                },
                            },
                        }),
                    },
                    colors:
                        seriesType === PanelSeriesType.SITE || seriesType === PanelSeriesType.SITE_CUMULATIVE
                            ? siteColours
                            : getThemeColours(theme),
                    credits: showWaterMark ? getCreditsStyle(`${t('cm:waterMarkMainPanelText')}`) : { enabled: false },
                    subtitle: showSubtitle
                        ? {
                              text: subTitle,
                              useHTML: true,
                              verticalAlign: 'bottom',
                              align: 'center',
                          }
                        : undefined,
                    legend: {
                        enabled: showLegend,
                        itemStyle: {
                            cursor: 'pointer',
                            textOverflow: 'ellipsis',
                        },
                        maxHeight: 100,
                    },
                    plotOptions: {
                        series: {
                            turboThreshold: TURBO_THRESHOLD,
                            marker: {
                                enabled: true,
                                symbol: 'circle',
                            },
                            stacking,
                        },
                    },
                    tooltip: {
                        formatter() {
                            const point = this as Point;

                            return ReactDOMServer.renderToStaticMarkup(
                                <div>
                                    {seriesType !== PanelSeriesType.SITE_AVERAGE && <b>{point.series.name}</b>}
                                    <br />
                                    {showxAxisLabels && (
                                        <>
                                            X-Axis:{' '}
                                            {isDateRange
                                                ? stringDateWithoutTimeFromUtc(new Date(point.x), 'ddd MMM D YYYY')
                                                : typeof point.x === 'number'
                                                  ? FormatNumberForChart(point.x)
                                                  : point.x}
                                            <br />
                                        </>
                                    )}
                                    Y-Axis: {FormatNumberForChart(point.y)}
                                </div>,
                            );
                        },
                    },
                    xAxis: {
                        title: { text: xTitle },
                        tickLength: showxAxisLabels ? 10 : 0,
                        type: isDateRange ? 'datetime' : 'linear',
                        categories,
                        crosshair: showCrosshairs && seriesType !== PanelSeriesType.SUBJECT_BY_VISIT,
                        labels: {
                            enabled: showxAxisLabels,
                            style: {
                                textOverflow: 'ellipsis',
                                width: 100,
                            },

                            events: {
                                mouseover(e: MouseEvent) {
                                    if (e.target) {
                                        setTargetElement(e.target);
                                        setOpen(true);
                                    }
                                },
                                mouseout() {
                                    setTargetElement(null);
                                    setOpen(false);
                                },
                            },
                        },
                    },
                    yAxis: {
                        title: { text: yTitle },
                        crosshair: showCrosshairs,
                        min: minY,
                        max: maxY,
                        plotLines:
                            horizontalLines === undefined
                                ? undefined
                                : horizontalLines.map<Highcharts.YAxisPlotLinesOptions>((pl) => ({
                                      color: pl.color,
                                      dashStyle: pl.style,
                                      label: { text: pl.name ?? undefined },
                                      value: pl.y,
                                      zIndex: 1,
                                  })),
                    },
                    series: panelData?.map((x) => ({
                        ...x,
                        data:
                            x.data.length < TURBO_THRESHOLD
                                ? x.data
                                : x.data.map((p) => {
                                      return [p.x, p.y];
                                  }),
                        marker: {
                            symbol: x.marker,
                        },
                        connectNulls: seriesType === PanelSeriesType.SUBJECT_BY_VISIT,
                        type: 'line',
                    })),
                },
                customOptions,
            ),
        );
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/exhaustive-deps
    }, [props]);

    if (options === null) return <Loading />;

    return (
        <Tooltip anchorElement="pointer" open={open} targetElement={targetElement} position="left">
            <HighchartsReact ref={chartRef} highcharts={Highcharts} constructorType="chart" options={options} />
        </Tooltip>
    );
};
export const LineChart = memo(Chart);
